<template>
  <v-container fluid class="mx-4 px-8">
    <v-row class="mt-2">
      <v-col cols="2">
        <v-text-field
          v-model="form.application_id"
          outlined
          dense
          hide-details="auto"
          maxlength="15"
          label="Application ID"
          @keyup.enter="getLoanPayments"
        ></v-text-field>
      </v-col>
      <v-col cols="2">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
        ></v-autocomplete>
      </v-col>
      <v-col cols="2">
        <v-select
          v-model="form.status"
          :items="statuses"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Loan Status"
        ></v-select>
      </v-col>
      <v-col cols="2">
        <v-select
          v-model="form.mapped"
          :items="mappedOptions"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Case Number"
        ></v-select>
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="getLoanPayments"
        >
          Search
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="loanPaymentHeaders"
            :items="loanPayments"
            :loading="loading"
            item-key="id"
            fixed-header
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
            <template v-slot:[`item.loan_case_number`]="{ item }">
              <span v-if="item.loan_case_number">{{
                item.loan_case_number
              }}</span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                v-if="item.status"
                small
                :color="statusColor(item.status)"
                text-color="white"
              >
                {{ item.status }}
              </v-chip>
              <span v-else class="grey--text">Initialized</span>
            </template>
            <template v-slot:[`item.lan_number`]="{ item }">
              {{ item.lan_number || "-" }}
            </template>
            <template v-slot:[`item.utr_number`]="{ item }">
              {{ item.utr_number || "-" }}
            </template>
            <template v-slot:[`item.utr_date`]="{ item }">
              {{ formatDate(item.utr_date) }}
            </template>
            <template v-slot:[`item.disbursed_amount`]="{ item }">
              {{ formatAmount(item.disbursed_amount) }}
            </template>
            <template v-slot:[`item.plan_amount_balance`]="{ item }">
              {{ formatAmount(item.plan_amount_balance) }}
            </template>
            <template v-slot:[`item.last_synced_at`]="{ item }">
              {{ formatDateTime(item.last_synced_at) }}
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-icon
                small
                color="accent"
                :title="
                  item.loan_case_number
                    ? 'Edit loan case number'
                    : 'Add loan case number'
                "
                @click="openCaseNumberDialog(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="caseNumberDialog" width="500" persistent>
      <v-card class="secondary">
        <v-form ref="caseNumberForm">
          <v-card-title class="font-weight-medium secondary--text primary">
            {{
              selected.loan_case_number
                ? "Edit Loan Case Number"
                : "Add Loan Case Number"
            }}
            <v-spacer></v-spacer>
            <v-btn small icon :disabled="saving" @click="closeCaseNumberDialog">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-5">
            <v-simple-table dense class="text-caption secondary mb-4">
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td class="font-weight-bold pr-4 pl-0">Name</td>
                    <td>{{ selected.name || "-" }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold pr-4 pl-0">Application ID</td>
                    <td>{{ selected.application_id || "-" }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.trim="caseNumber"
                  label="Loan Case Number*"
                  outlined
                  dense
                  maxlength="50"
                  hide-details="auto"
                  hint="The application number issued by the loan provider"
                  persistent-hint
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
            </v-row>
            <div class="mt-4 caption grey--text">
              The loan status and UTR are updated by the loan provider and
              cannot be edited here.
            </div>
          </v-card-text>

          <v-card-actions class="pt-0 mx-2">
            <v-spacer></v-spacer>
            <v-btn text :disabled="saving" @click="closeCaseNumberDialog">
              Cancel
            </v-btn>
            <v-btn class="primary" :loading="saving" @click="saveCaseNumber">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { loanPaymentHeaders } from "@/constants/headers";

export default {
  data() {
    return {
      rules,
      loanPaymentHeaders,
      // Opens on the same set as "Set Payment Options" — plans with money still
      // outstanding. Clear the Balance filter to include disbursed loans.
      form: { balance: "pending" },
      programs: [],
      statuses: [],
      loanPayments: [],
      loading: false,
      saving: false,
      caseNumberDialog: false,
      caseNumber: "",
      selected: {},
      mappedOptions: [
        { value: "true", text: "Mapped" },
        { value: "false", text: "Not mapped" },
      ],
      balanceOptions: [
        { value: "pending", text: "Pending" },
        { value: "settled", text: "Fully paid" },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Loan Payments" }]);
    this.init();
    this.getLoanPayments();
  },
  methods: {
    init() {
      Promise.all([this.getPrograms(), this.getStatuses()]);
    },
    objectToQueryParams(obj) {
      return Object.entries(obj)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== ""
        )
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&");
    },
    async getLoanPayments() {
      this.loading = true;
      try {
        // Partner scoping is applied by the backend from the session — the
        // client does not get to pick which partner it sees.
        const queryString = this.objectToQueryParams(this.form);
        const res = await this.$axios.get(`/loan_payments?${queryString}`);
        this.loanPayments = res.data.data;
      } catch (error) {
        this.showError("Failed to fetch loan payments", error);
      } finally {
        this.loading = false;
      }
    },
    async getStatuses() {
      try {
        const res = await this.$axios.get("/loan_statuses");
        this.statuses = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPrograms() {
      try {
        const url =
          this.currentUser.role === "Partner" ||
          this.currentUser.role === "Business" ||
          this.currentUser.role === "Business Reviewer"
            ? `/get_partner_programs/${this.currentUser.partner_id}`
            : "/programs";
        const response = await this.$axios.get(url);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    openCaseNumberDialog(item) {
      this.selected = item;
      this.caseNumber = item.loan_case_number || "";
      this.caseNumberDialog = true;
      this.$nextTick(() => this.$refs.caseNumberForm?.resetValidation());
    },
    closeCaseNumberDialog() {
      this.caseNumberDialog = false;
    },
    async saveCaseNumber() {
      if (!this.$refs.caseNumberForm.validate()) return;
      this.saving = true;
      try {
        await this.$axios.put("/loan_payments/loan_case_number", {
          applicant_detail_id: this.selected.applicant_detail_id,
          loan_case_number: this.caseNumber,
        });
        this.caseNumberDialog = false;
        this.$store.commit("showSnackbar", {
          message: "Loan case number saved",
        });
        await this.getLoanPayments();
      } catch (error) {
        this.showError("Failed to save loan case number", error);
      } finally {
        this.saving = false;
      }
    },
    statusColor(status) {
      if (["Disbursed", "Credit Approved", "Ops Approved"].includes(status))
        return "success";
      if (["Credit Rejected", "Ops Rejected", "Rejected"].includes(status))
        return "error";
      return "warning";
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(value) {
      if (!value) return "-";
      const [year, month, day] = value.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },
    formatDateTime(value) {
      if (!value) return "-";
      return `${this.formatDate(value)} ${value.slice(11, 19)}`;
    },
    showError(message, error) {
      console.error(message, error);
      this.$store.commit("showSnackbar", {
        message: error?.response?.data?.error?.message || message,
        color: "error",
      });
    },
  },
};
</script>

<style scoped></style>
